import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react';
import es from 'emoji-picker-react/dist/data/emojis-es';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { NewTemplateData } from "@/types";
import { ArrowRight, Check, X, Smile, Send, MessageSquare, Plus, FileText, ChevronDown, Type, Phone, ExternalLink, Trash2, Image as ImageIcon, Reply } from "lucide-react";
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import ImageUpload from "@/components/ImageUpload";
import {
  buildMetaTemplateRequest,
  checkVariableToWordRatio,
  countEmojis,
  extractTemplateVariables,
  normalizeMetaTemplateName,
  validateMetaTemplateDraft,
} from "@/lib/metaTemplateUtils";

interface CreateTemplateFormProps {
  newTemplate: NewTemplateData;
  setNewTemplate: React.Dispatch<React.SetStateAction<NewTemplateData>>;
  extractVariables: (template: string) => string[];
  handleCreateTemplate: (payload?: NewTemplateData) => Promise<void>;
  setShowCreateForm: (show: boolean) => void;
  isEditMode?: boolean;
  hideTitle?: boolean;
}

const CreateTemplateForm: React.FC<CreateTemplateFormProps> = ({
  newTemplate,
  setNewTemplate,
  extractVariables,
  handleCreateTemplate,
  setShowCreateForm,
  isEditMode = false,
  hideTitle = false
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showPredefinedVariables, setShowPredefinedVariables] = useState(false);
  const [submittingToMeta, setSubmittingToMeta] = useState(false);

  // Error states for validation
  const [nameError, setNameError] = useState(false);
  const [showVariablesPopover, setShowVariablesPopover] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Standalone create always uses UTILITY + Spanish (not user-selectable)
  useEffect(() => {
    if (isEditMode) return;
    setNewTemplate((prev) => ({
      ...prev,
      category: prev.category || 'Utilidad',
      language: 'es',
    }));
  }, [isEditMode, setNewTemplate]);

  // Header state
  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerType, setHeaderType] = useState<'TEXT' | 'IMAGE'>('TEXT');
  const [headerText, setHeaderText] = useState('');
  const [headerImageHandle, setHeaderImageHandle] = useState<string>('');
  const [headerImageUrl, setHeaderImageUrl] = useState<string>('');  // ✅ GCS public URL
  const [headerImagePreview, setHeaderImagePreview] = useState<string>('');

  // Footer state
  const [footerEnabled, setFooterEnabled] = useState(false);
  const [footerText, setFooterText] = useState('');

  // Buttons state
  type TemplateButton = {
    type: 'QUICK_REPLY' | 'PHONE_NUMBER' | 'URL';
    text: string;
    url?: string;
    phone_number?: string;
  };
  const [buttonsEnabled, setButtonsEnabled] = useState(false);
  const [buttons, setButtons] = useState<TemplateButton[]>([]);

  const handleNextWithValidation = () => {
    let ok = true;
    if (!newTemplate.name || !newTemplate.name.trim()) {
      setNameError(true);
      ok = false;
    } else {
      setNameError(false);
    }

    const draftValidation = validateMetaTemplateDraft({
      bodyText: newTemplate.template || '',
      headerEnabled,
      headerType,
      headerText,
      headerImageHandle,
      footerEnabled,
      footerText,
      buttonsEnabled,
      buttons,
    });

    if (!draftValidation.valid) {
      setValidationError(draftValidation.message);
      ok = false;
    } else {
      setValidationError(null);
    }

    if (!ok) return;
    handleSubmitToMeta();
  };

  // Submit template to Meta for approval
  const handleSubmitToMeta = async () => {
    try {
      setSubmittingToMeta(true);

      // Validate required fields
      if (!newTemplate.name || !newTemplate.template) {
        toast.error('Por favor completa el nombre y el contenido de la plantilla');
        if (!newTemplate.name?.trim()) setNameError(true);
        return;
      }

      const metaTemplateName = normalizeMetaTemplateName(newTemplate.name);

      if (!metaTemplateName || metaTemplateName.length === 0) {
        toast.error('El nombre de la plantilla debe contener letras o números (no solo caracteres especiales).');
        setSubmittingToMeta(false);
        return;
      }

      const metaTemplateRequest = buildMetaTemplateRequest({
        name: newTemplate.name,
        language: 'es',
        category: 'UTILITY',
        bodyText: newTemplate.template || '',
        headerEnabled,
        headerType,
        headerText,
        headerImageHandle,
        headerImageUrl,
        footerEnabled,
        footerText,
        buttonsEnabled,
        buttons,
      });

      // Debug: log the request being sent to Meta
      console.log('📤 Sending Meta template request:', JSON.stringify(metaTemplateRequest, null, 2));

      const response = await axios.post('/whatsapp/templates/create', metaTemplateRequest);

      if (response.data.success) {
        toast.success(
          `¡Plantilla enviada a Meta para aprobación! Estado: ${response.data.status}`,
          { duration: 5000 }
        );

        // ❌ REMOVED: We no longer call handleCreateTemplate here because
        // /whatsapp/templates/create already saves to db.templates (lines 319-336 in whatsapp_routes.py)
        // Calling handleCreateTemplate was causing:
        // 1. Duplicate entries in templates collection
        // 2. Second entry without header/footer/buttons data

        // Notify parent that template was created (for refreshing lists)
        if (handleCreateTemplate) {
          // Just call with empty to trigger refresh, actual template is already in DB
          handleCreateTemplate({ _skipInsert: true, id: response.data.template_id } as any);
        }

        // Close form after successful submission
        setTimeout(() => {
          setShowCreateForm(false);
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error submitting to Meta:', error);

      // Extraer mensaje de error de la respuesta
      const errorResponse = error.response?.data;
      let userMessage = '';

      if (errorResponse?.error_user_title || errorResponse?.error_user_msg) {
        // Error de validación de Meta - mostrar al usuario
        userMessage = errorResponse.error_user_title || 'Error de validación';
        if (errorResponse.error_user_msg) {
          userMessage += ': ' + errorResponse.error_user_msg;
        }
        toast.error(userMessage, { duration: 7000 });
      } else if (errorResponse?.detail) {
        // Error del backend
        toast.error('Error al crear la plantilla. Por favor intenta de nuevo.', { duration: 5000 });
      } else {
        // Error genérico
        toast.error('Error al enviar plantilla a Meta. Por favor intenta de nuevo.', { duration: 5000 });
      }
    } finally {
      setSubmittingToMeta(false);
    }
  };

  const whatsappToMarkdown = (text: string): string => {
    text = text.replace(/\*([^*]+)\*/g, '**$1**');
    text = text.replace(/_([^_]+)_/g, '*$1*');
    text = text.replace(/~([^~]+)~/g, '~~$1~~');
    return text;
  };

  const renderPreviewTextFromTemplate = (template: string) => {
    if (!template) return "Tu mensaje aparecerá aquí...";
    return template;
  };

  // Insertar emoji en el cursor
  const insertEmoji = (emojiData: any) => {
    const textarea = document.getElementById('templateContent') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = newTemplate.template || '';
    const before = text.substring(0, start);
    const after = text.substring(end);
    const newText = before + emojiData.emoji + after;

    setNewTemplate({ ...newTemplate, template: newText });

    setTimeout(() => {
      textarea.focus();
      const newPosition = start + emojiData.emoji.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Variables predefinidas organizadas
  const predefinedVariables = {
    cliente: [
      { name: 'Nombre', icon: '👤', description: 'Nombre del cliente' },
      { name: 'Apellido', icon: '👤', description: 'Apellido del cliente' },
      { name: 'Email', icon: '📧', description: 'Correo electrónico' },
      { name: 'Telefono', icon: '📱', description: 'Número de teléfono' },
    ],
    empresa: [
      { name: 'Empresa', icon: '🏢', description: 'Nombre de la empresa' },
      { name: 'Producto', icon: '📦', description: 'Nombre del producto' },
      { name: 'Factura', icon: '🧾', description: 'Número de factura' },
      { name: 'Monto', icon: '💰', description: 'Cantidad o monto' },
    ],
    otros: [
      { name: 'Fecha', icon: '📅', description: 'Fecha personalizada' },
      { name: 'Descuento', icon: '🎁', description: 'Descuento aplicado' },
    ]
  };

  // Insertar variable o texto en el cursor
  const insertVariable = (value: string, isVariable: boolean = true) => {
    const textarea = document.getElementById('templateContent') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = newTemplate.template || '';
    const before = text.substring(0, start);
    const after = text.substring(end);

    const insertion = isVariable ? `{{${value}}}` : value;
    const newText = before + insertion + after;

    setNewTemplate({ ...newTemplate, template: newText });

    setTimeout(() => {
      textarea.focus();
      const newPosition = start + insertion.length;
      textarea.setSelectionRange(newPosition, newPosition);
      if (isVariable) setShowVariablesPopover(false);
    }, 0);
  };

  // Single-screen create (message + name) — no metadata step
  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fadeIn">
      {/* Header igual que campañas */}
      {!isEditMode && !hideTitle && (
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Crear Nueva Plantilla</h1>
          <p className="text-gray-600 mt-1">Escribe el nombre y el contenido de tu plantilla</p>
        </div>
      )}

      <Card className="mt-6 relative">
        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
        <CardContent className="p-6 pl-8">
          <div className="space-y-6">
              <>
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                  {/* COLUMNA IZQUIERDA: Editor */}
                  <div className="space-y-4">
                    {/* Encabezado interna movido aquí para alineación horizontal */}
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full flex-none bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center">
                        <MessageSquare className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900 leading-tight">Crear Mensaje</div>
                        <p className="text-xs text-gray-400">Crea el mensaje utilizando datos; el editor y la vista previa se mostrarán aquí.</p>
                      </div>
                    </div>

                    {/* Nombre de la plantilla (required) */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-1">
                        Nombre de la plantilla
                      </label>
                      <input
                        id="templateName"
                        placeholder="Ejemplo: Recordatorio de citas"
                        value={newTemplate.name}
                        onChange={(e) => {
                          setNewTemplate({ ...newTemplate, name: e.target.value });
                          if (nameError) setNameError(false);
                        }}
                        className={`w-full cursor-text rounded-md bg-white py-1.5 pl-3 pr-3 text-sm shadow-sm border placeholder:text-gray-400 ${nameError ? 'border-rose-500 focus:ring-rose-500' : 'border-gray-200 hover:shadow-md'} focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] transition-all`}
                      />
                      {nameError && <p className="text-xs text-rose-600 mt-1">Introduce un nombre</p>}
                    </div>

                    {/* Datos Disponibles section with floating Popover */}
                    <div className="relative">
                      <label className="block text-sm font-semibold text-gray-900 mb-1">Datos disponibles</label>
                      <p className="text-[11px] text-gray-500 mb-2 leading-relaxed">
                        Selecciona un dato predefinido o inserta uno personalizado escribiendo cualquier nombre entre llaves dobles, por ejemplo: <span className="font-mono text-[#1CB3B3] font-bold">{"{{MiDato}}"}</span>.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowVariablesPopover(!showVariablesPopover)}
                        className="w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left shadow-sm border border-gray-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] sm:text-sm relative"
                      >
                        <span className="block truncate text-sm text-gray-400">Selecciona un dato</span>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <ChevronUpDownIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
                        </div>
                      </button>

                      {showVariablesPopover && (
                        <div className="absolute left-0 right-0 z-[9999] mt-1 bg-white rounded-md shadow-lg border border-gray-200 p-4">
                          <div className="space-y-4">
                            {/* Cliente */}
                            <div>
                              <p className="text-xs font-bold text-[#1CB3B3] mb-2 flex items-center gap-2">
                                <span>👤</span> Datos del Cliente
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {predefinedVariables.cliente.map(v => (
                                  <button
                                    key={v.name}
                                    onClick={() => { insertVariable(v.name); setShowVariablesPopover(false); }}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-gray-700 text-sm hover:border-[#1CB3B3] hover:text-[#1CB3B3] hover:bg-emerald-50 transition-all font-medium"
                                  >
                                    <span>{v.icon}</span>
                                    <span className="font-mono text-xs">{`{{${v.name}}}`}</span>
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Empresa */}
                            <div>
                              <p className="text-xs font-bold text-[#1CB3B3] mb-2 flex items-center gap-2">
                                <span>🏢</span> Datos de Empresa
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {predefinedVariables.empresa.map(v => (
                                  <button
                                    key={v.name}
                                    onClick={() => { insertVariable(v.name); setShowVariablesPopover(false); }}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-gray-700 text-sm hover:border-[#1CB3B3] hover:text-[#1CB3B3] hover:bg-emerald-50 transition-all font-medium"
                                  >
                                    <span>{v.icon}</span>
                                    <span className="font-mono text-xs">{`{{${v.name}}}`}</span>
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Otros */}
                            <div>
                              <p className="text-xs font-bold text-[#1CB3B3] mb-2 flex items-center gap-2">
                                <span>📌</span> Otros Campos
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {predefinedVariables.otros.map(v => (
                                  <button
                                    key={v.name}
                                    onClick={() => { insertVariable(v.name); setShowVariablesPopover(false); }}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-gray-700 text-sm hover:border-[#1CB3B3] hover:text-[#1CB3B3] hover:bg-emerald-50 transition-all font-medium"
                                  >
                                    <span>{v.icon}</span>
                                    <span className="font-mono text-xs">{`{{${v.name}}}`}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 pt-3 border-t border-slate-100">
                            <p className="text-[11px] text-slate-400 italic">
                              Haz clic en cualquier dato para insertarlo. Se cerrará automáticamente.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Editor area */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-semibold text-gray-900 block">Editor de mensaje</label>
                      </div>

                      <div className="relative group">
                        <textarea
                          id="templateContent"
                          value={newTemplate.template}
                          onChange={(e) => {
                            setNewTemplate({ ...newTemplate, template: e.target.value });
                            if (validationError) setValidationError(null);
                          }}
                          placeholder="Ej: Hola {{Nombre}}, tu factura {{Factura}} vence el {{Fecha}}."
                          className={`w-full h-[130px] max-h-[300px] overflow-y-auto resize-y rounded-md border p-3 text-sm shadow-sm focus:outline-none focus:ring-2 ${validationError ? "border-rose-500 focus:ring-rose-500" : "border-[#1CB3B3] focus:ring-[#1CB3B3]"} transition-all`}
                        />

                        {validationError && (
                          <div className="mt-3 p-4 bg-rose-50 border-2 border-rose-200 rounded-xl animate-fadeIn">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-none">
                                <span className="text-rose-600 text-lg">🚫</span>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-rose-800 mb-1">Plantilla no válida para Meta</p>
                                <p className="text-xs text-rose-700 leading-relaxed">{validationError}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="absolute right-4 bottom-4 flex gap-2">
                          {/* Botón flotante opcional si fuera necesario */}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="relative">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="flex items-center gap-2 rounded-md"
                          >
                            <span className="text-lg">😊</span>
                            Agregar emoji
                          </Button>

                          {showEmojiPicker && (
                            <div className="absolute bottom-full left-0 mb-2 z-50">
                              <EmojiPicker
                                onEmojiClick={(emojiData) => {
                                  insertVariable(emojiData.emoji, false);
                                  setShowEmojiPicker(false);
                                }}
                                emojiStyle={EmojiStyle.APPLE}
                                {...{ emojiData: es } as any}
                                theme={Theme.LIGHT}
                                width={320}
                                height={380}
                                searchPlaceholder="Buscar emoji..."
                                previewConfig={{ showPreview: false }}
                              />
                            </div>
                          )}
                        </div>

                        <Button
                          onClick={() => { setNewTemplate({ ...newTemplate, template: "" }); }}
                          className="bg-[#1CB3B3] hover:bg-[#179999] text-white font-semibold px-6 py-2 rounded-xl shadow-md transition-all active:scale-95"
                        >
                          Limpiar
                        </Button>
                      </div>




                      {/* Validación de variables detectadas + Indicador de proporción */}
                      {(() => {
                        const vars = extractVariables(newTemplate.template || '');
                        const ratioCheck = checkVariableToWordRatio(newTemplate.template || '');

                        if (vars.length === 0) return null;

                        const isRatioOk = ratioCheck.valid;
                        const wordsNeeded = !isRatioOk ? (ratioCheck.variables * 4 - ratioCheck.words) : 0;

                        return (
                          <div className="mt-3 space-y-2">
                            {/* Variables detectadas */}
                            <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                              <div className="flex items-center gap-2 mb-2">
                                <Check className="w-4 h-4 text-emerald-600" />
                                <span className="text-sm font-semibold text-emerald-800">
                                  {vars.length} dato{vars.length > 1 ? 's' : ''} detectado{vars.length > 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {vars.map((v, i) => (
                                  <span key={i} className="px-2 py-0.5 rounded-lg bg-white border border-emerald-200 text-emerald-700 font-mono text-xs">
                                    {`{{${v}}}`}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Indicador de proporción de variables */}
                            <div className={`p-4 rounded-xl border ${isRatioOk
                              ? 'bg-blue-50 border-blue-100'
                              : 'bg-amber-50 border-amber-200'}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-none ${isRatioOk ? 'bg-blue-100' : 'bg-amber-100'
                                  }`}>
                                  <span className="text-base">{isRatioOk ? '✓' : '⚠️'}</span>
                                </div>
                                <div>
                                  <p className={`text-sm font-semibold ${isRatioOk ? 'text-blue-800' : 'text-amber-800'}`}>
                                    {isRatioOk ? 'Proporción de texto válida' : 'Advertencia: Pocas palabras para tantos datos'}
                                  </p>
                                  <p className={`text-xs mt-0.5 ${isRatioOk ? 'text-blue-600' : 'text-amber-700'}`}>
                                    {isRatioOk
                                      ? `${ratioCheck.words} palabras para ${ratioCheck.variables} dato(s) - Meta aceptará esta proporción.`
                                      : `Tienes ${ratioCheck.words} palabras para ${ratioCheck.variables} dato(s). Agrega ~${wordsNeeded} palabras más o Meta rechazará la plantilla.`
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* ===== SECCIÓN: ELEMENTOS ADICIONALES ===== */}
                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-gray-900 mb-3">Elementos adicionales</label>
                        <p className="text-[11px] text-gray-500 mb-4 leading-relaxed">
                          Agrega encabezado, pie de página o botones a tu plantilla. Estos elementos son opcionales.
                        </p>
                      </div>

                      {/* ===== SECCIÓN: HEADER (TÍTULO) ===== */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setHeaderEnabled(!headerEnabled)}
                          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded flex items-center justify-center ${headerEnabled ? 'bg-[#1CB3B3] text-white' : 'bg-gray-200 text-gray-500'}`}>
                              <Type className="w-3.5 h-3.5" />
                            </div>
                            <div className="text-left">
                              <div className="text-xs font-semibold text-gray-900">Encabezado (Header)</div>
                              <div className="text-[10px] text-gray-500">Agrega un título o imagen al inicio</div>
                            </div>
                          </div>
                          <div className={`w-9 h-5 rounded-full transition-colors relative ${headerEnabled ? 'bg-[#1CB3B3]' : 'bg-gray-300'}`}>
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${headerEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </div>
                        </button>

                        {headerEnabled && (
                          <div className="px-3 py-3 border-t border-gray-200 bg-white space-y-3">
                            {/* Type selector */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-2">Tipo de encabezado</label>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  onClick={() => setHeaderType('TEXT')}
                                  className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border transition-all text-xs ${headerType === 'TEXT'
                                    ? 'border-[#1CB3B3] bg-[#1CB3B3]/10 text-[#1CB3B3]'
                                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                    }`}
                                >
                                  <Type className="w-4 h-4" />
                                  <span className="font-medium">Texto</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setHeaderType('IMAGE')}
                                  className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border transition-all text-xs ${headerType === 'IMAGE'
                                    ? 'border-[#1CB3B3] bg-[#1CB3B3]/10 text-[#1CB3B3]'
                                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                    }`}
                                >
                                  <ImageIcon className="w-4 h-4" />
                                  <span className="font-medium">Imagen</span>
                                </button>
                              </div>
                            </div>

                            {/* Conditional content based on type */}
                            {headerType === 'TEXT' ? (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Texto del encabezado
                                </label>

                                <input
                                  type="text"
                                  value={headerText}
                                  onChange={(e) => {
                                    const valueWithoutEmojis = e.target.value.replace(
                                      /\p{Extended_Pictographic}/gu,
                                      ''
                                    );

                                    setHeaderText(valueWithoutEmojis);
                                  }}
                                  placeholder="Ej: ¡Hola {{Nombre}}!"
                                  maxLength={60}
                                  className="w-full rounded border border-[#1CB3B3] px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                                />

                                <p className="text-[10px] text-gray-400 mt-0.5">
                                  {headerText.length}/60 caracteres. Máximo 1 variable en el encabezado, el encabezado no permite emojis.
                                </p>

                                {extractTemplateVariables(headerText).length > 1 && (
                                  <p className="text-[10px] text-rose-600 font-medium mt-1">
                                    El encabezado solo permite 1 variable. WhatsApp no acepta más de un dato en el header.
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Imagen del encabezado</label>
                                <ImageUpload
                                  onUploadComplete={(handle) => {
                                    setHeaderImageHandle(handle);
                                  }}
                                  onPublicUrlReady={(url) => {
                                    setHeaderImageUrl(url);  // ✅ Store GCS public URL
                                  }}
                                  onPreviewReady={(url) => {
                                    setHeaderImagePreview(url);
                                  }}
                                  onClear={() => {
                                    setHeaderImageHandle('');
                                    setHeaderImageUrl('');  // ✅ Clear GCS URL too
                                    setHeaderImagePreview('');
                                  }}
                                  headerHandle={headerImageHandle}
                                  previewUrl={headerImagePreview}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ===== SECCIÓN: FOOTER (PIE DE PÁGINA) ===== */}
                      <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setFooterEnabled(!footerEnabled)}
                          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded flex items-center justify-center ${footerEnabled ? 'bg-[#1CB3B3] text-white' : 'bg-gray-200 text-gray-500'}`}>
                              <FileText className="w-3.5 h-3.5" />
                            </div>
                            <div className="text-left">
                              <div className="text-xs font-semibold text-gray-900">Pie de página (Footer)</div>
                              <div className="text-[10px] text-gray-500">Texto pequeño al final del mensaje</div>
                            </div>
                          </div>
                          <div className={`w-9 h-5 rounded-full transition-colors relative ${footerEnabled ? 'bg-[#1CB3B3]' : 'bg-gray-300'}`}>
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${footerEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </div>
                        </button>

                        {footerEnabled && (
                          <div className="px-3 py-2 border-t border-gray-200 bg-white">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Texto del pie de página</label>
                            <input
                              type="text"
                              value={footerText}
                              onChange={(e) => setFooterText(e.target.value)}
                              placeholder="Ej: Responde STOP para cancelar"
                              maxLength={60}
                              className="w-full rounded border border-[#1CB3B3] px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                            />
                            <p className="text-[10px] text-gray-400 mt-0.5">{footerText.length}/60 caracteres. No permite datos.</p>
                          </div>
                        )}
                      </div>

                      {/* ===== SECCIÓN: BOTONES ===== */}
                      <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setButtonsEnabled(!buttonsEnabled)}
                          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded flex items-center justify-center ${buttonsEnabled ? 'bg-[#1CB3B3] text-white' : 'bg-gray-200 text-gray-500'}`}>
                              <Plus className="w-3.5 h-3.5" />
                            </div>
                            <div className="text-left">
                              <div className="text-xs font-semibold text-gray-900">Botones</div>
                              <div className="text-[10px] text-gray-500">Agrega hasta 3 botones interactivos</div>
                            </div>
                          </div>
                          <div className={`w-9 h-5 rounded-full transition-colors relative ${buttonsEnabled ? 'bg-[#1CB3B3]' : 'bg-gray-300'}`}>
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${buttonsEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </div>
                        </button>

                        {buttonsEnabled && (
                          <div className="px-3 py-2 space-y-2 border-t border-gray-200 bg-white">
                            {buttons.map((button, index) => (
                              <div key={index} className="p-2 border border-gray-200 rounded space-y-2 relative">
                                <button
                                  type="button"
                                  onClick={() => setButtons(buttons.filter((_, i) => i !== index))}
                                  className="absolute top-1.5 right-1.5 p-0.5 text-gray-400 hover:text-rose-500 transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>

                                <div className="text-[10px] font-semibold text-gray-500 uppercase">Botón {index + 1}</div>

                                <div className="grid grid-cols-3 gap-1.5">
                                  {[
                                    { value: 'QUICK_REPLY', icon: MessageSquare, label: 'Respuesta' },
                                    { value: 'URL', icon: ExternalLink, label: 'URL' },
                                    { value: 'PHONE_NUMBER', icon: Phone, label: 'Llamar' },
                                  ].map(({ value, icon: Icon, label }) => (
                                    <button
                                      key={value}
                                      type="button"
                                      onClick={() => {
                                        const newButtons = [...buttons];
                                        newButtons[index] = { ...newButtons[index], type: value as any };
                                        setButtons(newButtons);
                                      }}
                                      className={`flex items-center justify-center gap-1 py-1 px-1.5 rounded border transition-all text-[10px] ${button.type === value
                                        ? 'border-[#1CB3B3] bg-[#1CB3B3]/10 text-[#1CB3B3]'
                                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                        }`}
                                    >
                                      <Icon className="w-3 h-3" />
                                      <span className="font-medium">{label}</span>
                                    </button>
                                  ))}
                                </div>

                                <input
                                  type="text"
                                  value={button.text}
                                  onChange={(e) => {
                                    const newButtons = [...buttons];
                                    newButtons[index] = { ...newButtons[index], text: e.target.value };
                                    setButtons(newButtons);
                                  }}
                                  placeholder="Texto del botón"
                                  maxLength={25}
                                  className="w-full rounded border border-[#1CB3B3] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                                />

                                {button.type === 'URL' && (
                                  <input
                                    type="url"
                                    value={button.url || ''}
                                    onChange={(e) => {
                                      const newButtons = [...buttons];
                                      newButtons[index] = { ...newButtons[index], url: e.target.value };
                                      setButtons(newButtons);
                                    }}
                                    placeholder="https://tu-sitio.com/pagina"
                                    className="w-full rounded border border-[#1CB3B3] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                                  />
                                )}

                                {button.type === 'PHONE_NUMBER' && (
                                  <input
                                    type="tel"
                                    value={button.phone_number || ''}
                                    onChange={(e) => {
                                      const newButtons = [...buttons];
                                      newButtons[index] = { ...newButtons[index], phone_number: e.target.value };
                                      setButtons(newButtons);
                                    }}
                                    placeholder="+573001234567"
                                    className="w-full rounded border border-[#1CB3B3] px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3]"
                                  />
                                )}
                              </div>
                            ))}

                            {buttons.length < 3 && (
                              <button
                                type="button"
                                onClick={() => setButtons([...buttons, { type: 'QUICK_REPLY', text: '' }])}
                                className="w-full py-2 border border-dashed border-gray-300 rounded text-xs font-medium text-gray-600 hover:border-[#1CB3B3] hover:text-[#1CB3B3] transition-colors flex items-center justify-center gap-1.5"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Agregar botón ({buttons.length}/3)
                              </button>
                            )}

                            <p className="text-[10px] text-gray-400 leading-relaxed">
                              <strong>Respuesta:</strong> El usuario envía el texto.
                              <strong> URL:</strong> Abre enlace.
                              <strong> Llamar:</strong> Inicia llamada.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* COLUMNA DERECHA: Vista Previa */}
                  <div className="flex flex-col items-center pt-10 lg:pt-0 lg:self-stretch">
                    <div className="flex flex-col items-center lg:sticky lg:top-36">
                    {/* iPhone Frame */}
                    <div className="relative mx-auto w-[360px] h-[600px] rounded-[3rem] bg-gray-900 border border-black/10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col ring-1 ring-black/5">
                      {/* Status Bar */}
                      <div className="absolute top-0 w-full h-8 bg-transparent z-20 flex justify-between px-6 items-center pt-3">
                        <span className="text-[12px] text-white font-medium tracking-wide">
                          {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase()}
                        </span>
                        <div className="flex gap-2 items-center">
                          <div className="w-3.5 h-3.5 border-[1.5px] border-white rounded-full"></div>
                          <div className="w-5 h-2.5 border border-white rounded-[2px] relative flex p-[1px]">
                            <div className="bg-white h-full w-[90%] rounded-[0.5px]"></div>
                            <div className="absolute -right-[2px] top-[2px] h-[3px] w-[1.5px] bg-white rounded-r-[1px]"></div>
                          </div>
                        </div>
                      </div>

                      {/* Header WhatsApp */}
                      <div className="flex items-center justify-between px-4 py-3 pb-3 pt-10 flex-none bg-[#075E54] z-10 shadow-sm">
                        <div className="flex items-center gap-3 text-white">
                          <div className="w-5 -ml-1">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"></path></svg>
                          </div>
                          <img src="https://i.pravatar.cc/120?img=32" alt="avatar" className="w-10 h-10 rounded-full object-cover border border-white/20" />
                          <div className="leading-tight">
                            <div className="text-[15px] font-semibold text-white">+57 ··· ··· ····</div>
                            <div className="text-[11px] text-white/90">en línea</div>
                          </div>
                        </div>
                      </div>

                      {/* Chat Background */}
                      <div className="bg-[#efeae2] p-3 flex-1 overflow-hidden flex flex-col relative" style={{ minHeight: 0 }}>
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')",
                            backgroundSize: "400px",
                            opacity: 0.4
                          }}
                        />

                        {/* Messages Area - automatically scrolls from top to bottom */}
                        <div className="flex-1 flex flex-col overflow-y-auto p-3" style={{ maxHeight: '380px' }}>
                          <div className="flex justify-end mt-auto">
                            <div className="max-w-[84%] relative z-10">
                              {/* Message Bubble Container - grows naturally to fit content */}
                              <div className="bg-[#D9FDD3] rounded-lg shadow-sm overflow-hidden" style={{ borderTopLeftRadius: '8px', borderTopRightRadius: '0px', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>

                                {/* HEADER Preview - TEXT or IMAGE */}
                                {headerEnabled && headerType === 'TEXT' && headerText.trim() && (
                                  <div className="px-4 pt-3 pb-1">
                                    <p className="font-bold text-sm text-gray-900 break-words">{headerText}</p>
                                  </div>
                                )}
                                {headerEnabled && headerType === 'IMAGE' && headerImagePreview && (
                                  <div className="w-full">
                                    <img
                                      src={headerImagePreview}
                                      alt="Header"
                                      className="w-full h-auto object-cover max-h-[150px]"
                                    />
                                  </div>
                                )}

                                {/* BODY */}
                                <div className="px-4 py-2">
                                  <div className="text-sm text-black leading-relaxed break-words whitespace-pre-wrap [&_img]:inline [&_img]:w-5 [&_img]:h-5 [&_img]:align-text-bottom [&_img]:mx-[1px]">
                                    {newTemplate.template ? (
                                      <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                          p: ({ children }) => <p className="m-0">{children}</p>,
                                          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                                          em: ({ children }) => <em className="italic">{children}</em>,
                                          del: ({ children }) => <del className="line-through">{children}</del>,
                                        }}
                                      >
                                        {whatsappToMarkdown(newTemplate.template)}
                                      </ReactMarkdown>
                                    ) : (
                                      <p className="text-gray-500">Tu mensaje aparecerá aquí...</p>
                                    )}
                                  </div>
                                </div>

                                {/* FOOTER Preview */}
                                {footerEnabled && footerText.trim() && (
                                  <div className="px-4 pb-2">
                                    <p className="text-xs text-gray-500 break-words">{footerText}</p>
                                  </div>
                                )}

                                {/* Timestamp */}
                                <div className="text-[10px] text-gray-500 px-4 pb-2 text-right">
                                  {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false })}
                                  <span className="ml-1 text-[#34B7F1]">✓✓</span>
                                </div>
                              </div>

                              {/* BUTTONS Preview - Outside bubble like WhatsApp */}
                              {buttonsEnabled && buttons.length > 0 && (
                                <div className="mt-[2px] overflow-hidden rounded-lg bg-white/95 shadow-sm divide-y divide-gray-200">
                                  {buttons.filter(b => b.text.trim()).map((button, idx) => (
                                    <div
                                      key={idx}
                                      className="py-2.5 px-4 text-center"
                                    >
                                      <span className="text-sm font-medium text-[#00A884] flex items-center justify-center gap-2">
                                        {button.type === 'URL' && <ExternalLink className="w-4 h-4" />}
                                        {button.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4" />}
                                        {button.type === 'QUICK_REPLY' && <Reply className="w-4 h-4" />}
                                        {button.text}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Input bar mockup */}
                        <div className="mt-3 bg-white rounded-full px-3 py-2 flex items-center gap-3 shadow-sm flex-none">
                          <div className="w-9 h-9 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-green-600 text-xl font-bold">+</div>
                          <div className="flex items-center gap-2 flex-1">
                            <div className="flex-1 text-base text-gray-500 select-none">Escribe un mensaje...</div>
                          </div>
                          <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center text-white text-sm">✓</div>
                        </div>
                      </div>
                    </div>

                    {/* Explanatory text below phone preview */}
                    <div className="mt-4 text-center max-w-[360px]">
                      <p className="text-sm text-slate-500 leading-relaxed">
                        Este es un ejemplo de cómo verán el mensaje tus clientes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              

                {/* ✅ MODIFICADO: Botones de navegación con texto dinámico */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                    className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-11 rounded-xl shadow-sm"
                  >
                    ← Cancelar
                  </Button>
                  <Button
                    onClick={handleNextWithValidation}
                    disabled={!newTemplate.template?.trim() || !newTemplate.name?.trim() || submittingToMeta}
                    className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-8 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95 disabled:opacity-70 disabled:pointer-events-none"
                  >
                    {submittingToMeta ? (
                      <div className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enviando a Meta...
                      </div>
                    ) : (
                      isEditMode ? 'Guardar y Enviar' : 'Crear Plantilla '
                    )}
                  </Button>
                </div>
              </>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateTemplateForm;
